/**
 * React hooks for reading/writing system settings via generalized endpoints.
 */
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { ReadSettingsResponse, WriteResult, SettingsChange, DryRunValidation } from '@/lib/control-types';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('No active session');
  return { Authorization: `Bearer ${session.access_token}` };
}

/**
 * Hook for reading system settings.
 */
export function useReadSettings() {
  const [data, setData] = useState<ReadSettingsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const read = useCallback(async (systemId: string, deviceSn: string, saveSnapshot = false) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const { data: result, error: fnError } = await supabase.functions.invoke<ReadSettingsResponse>(
        'system-read-settings',
        {
          body: { systemId, deviceSn, saveSnapshot },
          headers,
        }
      );
      if (fnError) throw fnError;
      if (!result?.success) throw new Error('Failed to read settings');
      setData(result);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to read settings';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return { data, loading, error, read, clear };
}

/**
 * Hook for writing system settings.
 */
export function useWriteSettings() {
  const [result, setResult] = useState<WriteResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const write = useCallback(async (
    systemId: string,
    deviceSn: string,
    changes: SettingsChange[]
  ): Promise<WriteResult | null> => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const headers = await getAuthHeaders();
      const { data: res, error: fnError } = await supabase.functions.invoke<WriteResult>(
        'system-write-settings',
        {
          body: { systemId, deviceSn, changes },
          headers,
        }
      );
      if (fnError) throw fnError;
      setResult(res);
      return res;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to write settings';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { result, loading, error, write };
}

/**
 * Hook for dry-run validation of settings changes.
 */
export function useDryRunSettings() {
  const [validation, setValidation] = useState<DryRunValidation[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validate = useCallback(async (
    systemId: string,
    deviceSn: string,
    changes: SettingsChange[]
  ): Promise<DryRunValidation[] | null> => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const { data: res, error: fnError } = await supabase.functions.invoke<{ dryRun: boolean; validation: DryRunValidation[] }>(
        'system-write-settings',
        {
          body: { systemId, deviceSn, changes, dryRun: true },
          headers,
        }
      );
      if (fnError) throw fnError;
      setValidation(res?.validation ?? null);
      return res?.validation ?? null;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Validation failed';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { validation, loading, error, validate };
}
